<template>
  <div class="employee-detail">
    <!-- Show header and menu only when not inspecting -->
    <template v-if="!showInspectForm">
      <EmployeePageHeader
        :employeeId="computedEmployeeId"
        :employeeData="employeeData"
        :status="employeeStatus"
        :name="employeeName"
        :avatar="employeeData?.avatar || ''"
        :title="employeePositionTitle"
        :archived="employeeData?.archived || false"
        :tracking="employeeData?.tracking !== false"
        :readiness="
          employeeData?.primary_readiness ?? employeeData?.current_readiness ?? 0
        "
        :last_month_readiness="employeeData?.last_month_readiness ?? null"
        :breadcrumbs="computedBreadcrumbs"
        @employeeUpdated="handleEmployeeUpdate"
        @employeeStatusChanged="handleEmployeeStatusChanged"
        @employeeDeleted="handleEmployeeDeleted"
        @breadcrumb-click="handleBreadcrumbClick"
      />
      <EmployeeHorizontalMenu
        :menuItems="menuItems"
        :initialTab="selectedTabLabel"
        @tab-selected="onTabChange"
      />
    </template>
    <!-- Show InspectToAwardForm as full page when inspecting -->
    <div v-if="showInspectForm && selectedResponsibility" class="inspect-form-page-wrapper">
      <InspectToAwardForm
        :breadcrumbsList="inspectionBreadcrumbs"
        :employee="formattedEmployee"
        :responsibility="formattedResponsibility"
        :inspectionData="inspectionData"
        :filterText="inspectionFilterText"
        :showNoRecords="showNoInspectionRecords"
        :showPagination="showInspectionPagination"
        :pagination="inspectionPagination"
        :chipText="inspectionChipText"
        :filterGroups="inspectionFilterGroups"
        :filterType="inspectionFilterType"
        :statusOptions="inspectionStatusOptions"
        :groupsItems="inspectionGroupsItems"
        :levelCounter="inspectionLevelCounter"
        :currentBadge="currentBadgeLevel"
        :readyForLevel="readyForBadgeLevel"
        :dot="inspectionDot"
        @onClickBreadcrumb="handleInspectionBreadcrumbClick"
        @badge-selected="handleBadgeSelectedFromInspection"
        @onSearch="handleInspectionSearch"
        @onClick="handleInspectionClick"
        @removeFilter="handleInspectionRemoveFilter"
        @filterTypes="handleInspectionFilterTypes"
        @onPaginationRequest="handleInspectionPagination"
        @clearSelection="handleInspectionClearSelection"
        @onCheckedItemsChanged="handleInspectionCheckedItemsChanged"
        @onPass="handleInspectionPass"
        @onFail="handleInspectionFail"
        @onNotApplicable="handleInspectionNotApplicable"
        @onClose="handleCloseInspection"
      />
    </div>

    <!-- Show Employee Detail content when not inspecting -->
    <div v-else class="employee-detail-content">
      <!-- Path to Readiness Tab -->
      <div
        v-if="selectedTab === 'pathtoreadiness' && employeeData"
        class="tab-content-wrapper"
      >
        <NewEmployeePathToReadinessView
          ref="pathToReadinessRef"
          :key="`pathtoreadiness-${computedEmployeeId}`"
          @employeeStatusChanged="handleEmployeeStatusChanged"
          :read_only="selectedTab === 'pathtoreadiness' ? false : true"
        />
      </div>

      <!-- Responsibilities Tab - Same as Path to Readiness for now -->
      <div
        v-if="selectedTab === 'responsibilities' && employeeData"
        class="tab-content-wrapper"
      >
        <NewEmployeePathToReadinessView
          :key="`responsibilities-${computedEmployeeId}`"
          @employeeStatusChanged="handleEmployeeStatusChanged"
        />
      </div>

      <!-- Badges Tab -->
      <div
        v-if="selectedTab === 'badges' && employeeData"
        class="tab-content-wrapper"
      >
        <EmployeeBadges
          ref="employeeBadgesRef"
          :key="`badges-${computedEmployeeId}`"
          @employeeUpdated="handleEmployeeUpdate"
          @employeeStatusChanged="handleEmployeeStatusChanged"
          @issue-badge-clicked="handleIssueBadgeClick"
          @remove-badge="handleRemoveBadgeClick"
        />
      </div>

      <!-- Positions History Tab -->
      <div
        v-if="selectedTab === 'positionshistory' && employeeData"
        class="tab-content-wrapper"
      >
        <EmployeeAssignmentView
          :key="`positionshistory-${computedEmployeeId}`"
          @employeeUpdated="handleEmployeeUpdate"
          @employeeStatusChanged="handleEmployeeStatusChanged"
        />
      </div>
    </div>

    <!-- Badge Issuance Workflow -->
    <!-- Step 1: Pick Responsibility Dialog -->
    <PickResponsibilityToIssueABadgeDialog
      v-model="showPickResponsibilityDialog"
      :items="availableResponsibilities"
      :hasMoreData="hasMoreResponsibilities"
      :isLoadingMore="isLoadingResponsibilities"
      @next="handleResponsibilitySelected"
      @close="handleClosePickResponsibility"
      @search="handleSearchResponsibilities"
      @load-more="handleLoadMoreResponsibilities"
    />


    <!-- Step 3: Give Badge Dialog (when responsibility has NO active checkpoints) -->
    <GiveBadgeDialog
      v-model="showGiveBadgeDialog"
      :description="giveBadgeDescription"
      :employeeName="employeeName"
      :responsibilityName="selectedResponsibility?.name || ''"
      :canNotify="canNotifyEmployee"
      :defaultLevel="selectedBadgeLevel"
      @give-badge="handleGiveBadge"
      @close="handleCloseGiveBadge"
    />

    <!-- Reason Modal for Fail and Not Applicable -->
    <ReasonModal
      v-if="reasonModal.show"
      :showDialog="reasonModal.show"
      :title="reasonModal.config.title"
      :description="reasonModal.config.description"
      :placeHolder="reasonModal.config.placeHolder"
      :btnLabel="reasonModal.config.btnLabel"
      :btnDanger="reasonModal.config.btnDanger"
      :btnColor="reasonModal.config.btnColor"
      :value="reason"
      @input="(val) => reason = val"
      @onAction="handleReasonAction"
      @onClose="closeReasonModal"
    />

    <!-- Remove Badge Dialog -->
    <RemoveBadgeDialog
      v-model="showRemoveBadgeDialog"
      :badgeName="selectedBadgeForRemoval?.badgeName || selectedBadgeForRemoval?.level || ''"
      :responsibilityName="selectedBadgeForRemoval?.responsibilityName || selectedBadgeForRemoval?.responsibility?.name || ''"
      :employeeName="employeeName || employeeData?.name || ''"
      @remove="handleConfirmRemoveBadge"
      @close="handleCloseRemoveBadgeDialog"
    />
  </div>
</template>

<script setup>
import {
  defineProps,
  ref,
  computed,
  onMounted,
  watch,
  getCurrentInstance,
  nextTick,
} from "vue";
import EmployeePageHeader from "@components/EmployeePageHeader/EmployeePageHeader.vue";
import EmployeeHorizontalMenu from "@components/EmployeeHorizontalMenu/EmployeeHorizontalMenu.vue";
import NewEmployeePathToReadinessView from "@/views/DetailEmployeeViews/NewEmployeePathToReadinessView.vue";
import EmployeeBadges from "@components/EmployeeBadges/EmployeeBadges.vue";
import EmployeeAssignmentView from "@/views/DetailEmployeeViews/EmployeeAssignmentView.vue";
import PickResponsibilityToIssueABadgeDialog from "@components/PickResponsibilityToIssueABadgeDialog/PickResponsibilityToIssueABadgeDialog.vue";
import InspectToAwardForm from "@components/InspectToAwardForm/InspectToAwardForm.vue";
import GiveBadgeDialog from "@components/GiveBadgeDialog/GiveBadgeDialog.vue";
import ReasonModal from "@components/ReasonModal/ReasonModal.vue";
import RemoveBadgeDialog from "@components/RemoveBadgeDialog/RemoveBadgeDialog.vue";
import { 
  getEmployeeByIdV2,
  getListofResponsibilityForBadges,
  createProficiencyBadge,
  getResponsibilityIdV2,
  getResponsibilityChecklistV2,
  getListActiveCheckpointsEvalV2,
  passCheckPointEvaluation,
  failCheckPointEvaluation,
  notApplicableCheckPointEvaluation,
  deleteResponsibilityBadge
} from "@/api";
import { useEmployeeStore } from "@/store/pinia/employee";
import pathToReadiness from "@components/assets/icons/path-to-rediness.svg";
import positionHistory from "@components/assets/icons/position-history.svg";
import badges from "@components/assets/icons/horizontal-badge.svg";

const props = defineProps({
  employeeId: {
    type: [Number, String],
    default: null,
  },
  breadcrumbsList: {
    type: Array,
    default: () => [],
  },
});

const instance = getCurrentInstance();
const proxy = instance?.proxy;
const route = proxy?.$route;

const emit = defineEmits(["backToList", "employeeUpdated", "breadcrumb-click"]);

const selectedTab = ref("pathtoreadiness");
const employeeData = ref(null);
const accountId = ref(null);
const employeeId = ref(null);
const employeeStore = useEmployeeStore();
const pathToReadinessRef = ref(null);

// Badge Issuance Workflow State
const showPickResponsibilityDialog = ref(false);
const showInspectForm = ref(false);
const showGiveBadgeDialog = ref(false);
const selectedResponsibility = ref(null);
const availableResponsibilities = ref([]);
const hasMoreResponsibilities = ref(false);
const isLoadingResponsibilities = ref(false);
const responsibilitySearchTerm = ref("");
const responsibilityPage = ref(1);

// Inspection Form State
const inspectionData = ref([]);
const inspectionFilterText = ref("");
const showNoInspectionRecords = ref(false);
const showInspectionPagination = ref(false);
const inspectionPagination = ref({ total: 0, page: 1, perPage: 10 });
const inspectionChipText = ref([]);
const inspectionFilterGroups = ref([]);
const inspectionFilterType = ref("Checkpoint");
const inspectionStatusOptions = ref([]);
const inspectionGroupsItems = ref([]);
const inspectionLevelCounter = ref({
  Apprentice: 0,
  Professional: 0,
  Coach: 0,
  Master: 0,
});
const currentBadgeLevel = ref(null);
const readyForBadgeLevel = ref(null);
const inspectionDot = ref("Good Standing");
const formattedResponsibility = ref(null);
const formattedEmployee = ref(null);
const inspectionBreadcrumbs = ref([
  { text: "Employee", disabled: false },
  { text: "Issue Badge", disabled: true },
]);

// Reason modal state for Fail and Not Applicable
const reasonModal = ref({
  show: false,
  type: null,
  config: {},
});
const modalConfigs = {
  fail: {
    title: "Add Criteria Failed Reason",
    description: "Please provide a reason why this criteria was not met.",
    placeHolder: "Explain why the criteria was not fulfilled or provide specific feedback.",
    btnLabel: "Fail",
    btnDanger: true,
    btnColor: null,
  },
  notApplicable: {
    title: "Mark Criteria as Not Applicable",
    description: "Provide a reason why this criteria is not applicable.",
    placeHolder: "Explain why the criteria is not applicable.",
    btnLabel: "Mark as Not Applicable",
    btnDanger: false,
    btnColor: "#666666",
  },
};
const reason = ref("");
const selectedCheckpoint = ref(null);

// Remove Badge Dialog State
const showRemoveBadgeDialog = ref(false);
const selectedBadgeForRemoval = ref(null);
const employeeBadgesRef = ref(null);

// Give Badge Dialog State
const selectedBadgeLevel = ref("apprentice");
const canNotifyEmployee = computed(() => {
  return employeeData.value?.access?.is_suspended === false;
});
const giveBadgeDescription = computed(() => {
  if (selectedResponsibility.value?.name) {
    return `Responsibility ${selectedResponsibility.value.name} has no Readiness Checklist therefore you can only Give (Instead of Awarding) a Badge to employee ${employeeName.value}.`;
  }
  return "";
});

const menuItems = ref([
  {
    label: "Path to Readiness",
    icon: pathToReadiness,
    value: "pathtoreadiness",
    enabled: true,
  },
  {
    label: "Badges",
    icon: badges,
    value: "badges",
    enabled: true,
  },
  {
    label: "Positions History",
    icon: positionHistory,
    value: "positionshistory",
    enabled: true,
  },
]);

const computedEmployeeId = computed(() => {
  return (
    props.employeeId ||
    route?.params?.employeeId ||
    route?.query?.employeeId ||
    null
  );
});

const employeeName = computed(() => {
  if (employeeData.value?.first_name && employeeData.value?.last_name) {
    return `${employeeData.value.first_name} ${employeeData.value.last_name}`;
  }
  return employeeData.value?.first_name || employeeData.value?.last_name || "";
});

const employeeStatus = computed(() => {
  if (employeeData.value?.archived) return "archived";
  return employeeData.value?.current_status || employeeData.value?.status || "";
});

const employeePositionTitle = computed(() => {
  // Get the most up-to-date employee data from either local ref or store
  const currentEmployee = employeeData.value || employeeStore.currentEmployee;
  
  if (!currentEmployee) {
    return "";
  }
  
  // Don't show position for terminated employees
  const status = currentEmployee?.current_status || currentEmployee?.status || "";
  if (status.toLowerCase() === "terminated") {
    return "";
  }
  
  // Use primary_position name if available, otherwise fall back to title
  return currentEmployee?.primary_position?.name || currentEmployee?.title || "";
});

const computedBreadcrumbs = computed(() => {
  if (props.breadcrumbsList && props.breadcrumbsList.length > 0) {
    return props.breadcrumbsList;
  }

  const currentPath = route?.path || "";
  const employeesHref = currentPath.includes("/new-organization")
    ? "/new-organization/employees"
    : "/organization/employees";

  return [
    {
      text: "Employees",
      disabled: false,
      href: employeesHref,
    },
    {
      text: "Employee",
      disabled: true,
    },
  ];
});

const selectedTabLabel = computed(() => {
  const item = menuItems.value.find((item) => item.value === selectedTab.value);
  return item ? item.label : "Path to Readiness";
});

function onTabChange(tabLabel) {
  const item = menuItems.value.find((item) => item.label === tabLabel);
  if (item) {
    selectedTab.value = item.value;
    if (proxy.$router && route) {
      // Check if we're on the new organization route
      const isNewOrganization = route?.name === "NewOrganization" || 
                                 route?.path?.includes("/new-organization");
      
      if (isNewOrganization) {
        // For new organization, navigate to NewOrganization route with query parameters
        const employeeId = computedEmployeeId.value || route?.query?.employeeId;
        proxy.$router
          .replace({
            name: "NewOrganization",
            params: { tab: "employees" },
            query: {
              employeeId: employeeId,
              employeeTab: item.value,
            },
          })
          .catch(() => {});
      } else {
        // For old organization routes, use the existing behavior
        proxy.$router
          .replace({
            ...route,
            query: {
              ...route.query,
              employeeTab: item.value,
            },
          })
          .catch(() => {});
      }
    }
  }
}

function handleBreadcrumbClick(breadcrumb) {
  if (breadcrumb.text === "Employees") {
    emit("backToList");
  }
}

async function fetchEmployeeData(forceRefresh = false) {
  try {
    if (!accountId.value || !employeeId.value) {
      console.warn("Missing accountId or employeeId:", {
        accountId: accountId.value,
        employeeId: employeeId.value,
      });
      return;
    }

    // If forceRefresh is true, use store's fetchCurrentEmployee to bypass cache
    if (forceRefresh) {
      const freshData = await employeeStore.fetchCurrentEmployee(
        employeeId.value,
        true
      );
      if (freshData) {
        employeeData.value = { ...freshData };
        if (proxy.$store) {
          proxy.$store.commit("employeeStore/setCurrentEmployee", freshData);
        }
        employeeStore.setCurrentEmployee(freshData);
        await nextTick();
        return;
      }
    }

    const { data } = await getEmployeeByIdV2({
      account_id: accountId.value,
      employeeId: employeeId.value,
    });

    if (data) {
      // Create a new object reference to ensure reactivity
      employeeData.value = { ...data };
      // Update both Vuex and Pinia stores so that EmployeeHorizontalSideBar can access the correct employee
      if (proxy.$store) {
        proxy.$store.commit("employeeStore/setCurrentEmployee", data);
      }
      // Also update Pinia store
      employeeStore.setCurrentEmployee(data);
      
      // Wait for Vue to recognize the change
      await nextTick();

      // If readiness is 0, it might need recalculation, but don't force it here
      // The EmployeeHorizontalSideBar component will handle recalculation if needed
      // Just ensure we're using the most up-to-date value
    } else {
      console.warn("No employee data found for ID:", employeeId.value);
      employeeData.value = null;
    }
  } catch (error) {
    console.error("Error fetching employee data:", error);
    employeeData.value = null;
  }
}

async function handleEmployeeUpdate() {
  try {
    // Force refresh employee data from API to get latest position assignments
    // Use store's fetchCurrentEmployee with forceRefresh=true to bypass cache
    const freshData = await employeeStore.fetchCurrentEmployee(
      computedEmployeeId.value,
      true // forceRefresh
    );
    
    if (freshData) {
      // Create new object reference to ensure reactivity
      employeeData.value = { ...freshData };
      
      // Update stores
      if (proxy.$store) {
        proxy.$store.commit("employeeStore/setCurrentEmployee", freshData);
      }
      employeeStore.setCurrentEmployee(freshData);
      
      // Wait for Vue to recognize the change
      await nextTick();
    } else {
      // Fallback to direct API call if store method fails
      await fetchEmployeeData();
    }
    
    // Delayed API refresh after 2-3 seconds to ensure backend has processed the changes
    setTimeout(async () => {
      try {
        const delayedFreshData = await employeeStore.fetchCurrentEmployee(
          computedEmployeeId.value,
          true // forceRefresh
        );
        if (delayedFreshData) {
          employeeData.value = { ...delayedFreshData };
          if (proxy.$store) {
            proxy.$store.commit("employeeStore/setCurrentEmployee", delayedFreshData);
          }
          employeeStore.setCurrentEmployee(delayedFreshData);
          await nextTick();
        }
      } catch (error) {
        console.error("Error in delayed employee data refresh:", error);
      }
    }, 2500); // 2.5 seconds delay
  } catch (error) {
    console.error("Error updating employee data:", error);
    // Fallback to direct API call on error
    await fetchEmployeeData();
  }
  
  emit("employeeUpdated");
}

async function handleEmployeeStatusChanged() {
  try {
    // Force refresh employee data from API to get latest status and position
    // Use store's fetchCurrentEmployee with forceRefresh=true to bypass cache
    const freshData = await employeeStore.fetchCurrentEmployee(
      computedEmployeeId.value,
      true // forceRefresh
    );
    
    if (freshData) {
      // CRITICAL: Normalize status field - ensure both status and current_status are set
      // This ensures child component watchers trigger properly
      const normalizedEmployeeData = {
        ...freshData,
        status: freshData.status || freshData.current_status,
        current_status: freshData.current_status || freshData.status,
      };
      
      // Create new object reference to ensure reactivity
      employeeData.value = { ...normalizedEmployeeData };
      
      // Update stores with normalized data
      if (proxy.$store) {
        proxy.$store.commit("employeeStore/setCurrentEmployee", normalizedEmployeeData);
      }
      employeeStore.setCurrentEmployee(normalizedEmployeeData);
      
      // Wait for Vue to recognize the change
      await nextTick();
      
      // If status is terminated, ensure child component reacts immediately
      const statusToCheck = (
        normalizedEmployeeData.current_status ||
        normalizedEmployeeData.status ||
        ""
      ).toLowerCase();
      
      if (statusToCheck === "terminated") {
        // Force immediate reactivity update by creating a new object reference
        // This ensures child component watchers detect the change
        employeeData.value = { ...normalizedEmployeeData };
        await nextTick();
        
        // Also directly call child component's handleEmployeeStatusChanged if available
        // This ensures immediate reaction even if watchers don't trigger
        if (pathToReadinessRef.value && typeof pathToReadinessRef.value.handleEmployeeStatusChanged === 'function') {
          await pathToReadinessRef.value.handleEmployeeStatusChanged();
        }
      }
    } else {
      // Fallback to direct API call if store method fails
      await fetchEmployeeData();
    }
    
    // Delayed API refresh after 2-3 seconds to ensure backend has processed the changes
    setTimeout(async () => {
      try {
        const delayedFreshData = await employeeStore.fetchCurrentEmployee(
          computedEmployeeId.value,
          true // forceRefresh
        );
        if (delayedFreshData) {
          // Normalize status for delayed update as well
          const normalizedDelayedData = {
            ...delayedFreshData,
            status: delayedFreshData.status || delayedFreshData.current_status,
            current_status: delayedFreshData.current_status || delayedFreshData.status,
          };
          
          employeeData.value = { ...normalizedDelayedData };
          if (proxy.$store) {
            proxy.$store.commit("employeeStore/setCurrentEmployee", normalizedDelayedData);
          }
          employeeStore.setCurrentEmployee(normalizedDelayedData);
          await nextTick();
        }
      } catch (error) {
        console.error("Error in delayed employee status refresh:", error);
      }
    }, 2500); // 2.5 seconds delay
  } catch (error) {
    console.error("Error updating employee status:", error);
    // Fallback to direct API call on error
    await fetchEmployeeData();
  }
}

function handleEmployeeDeleted() {
  emit("backToList");
}

// Badge Issuance Workflow Handlers
function handleIssueBadgeClick() {
  showPickResponsibilityDialog.value = true;
  responsibilityPage.value = 1;
  responsibilitySearchTerm.value = "";
  availableResponsibilities.value = [];
  loadResponsibilities();
}

async function loadResponsibilities(isLoadMore = false) {
  if (!accountId.value) return;
  
  try {
    isLoadingResponsibilities.value = true;
    const limit = 10;
    const offset = isLoadMore ? (responsibilityPage.value - 1) * limit : 0;
    
    const response = await getListofResponsibilityForBadges({
      accountId: accountId.value,
      limit,
      offset,
      name: responsibilitySearchTerm.value,
      status: "active",
    });
    
    const responsibilities = response.data?.results || [];
    const newItems = responsibilities.map((item) => ({
      id: item.id,
      code: item.code_str?.match(/\d+/)?.[0] || item.code,
      name: item.name,
      status: item.status,
    }));
    
    if (isLoadMore) {
      availableResponsibilities.value = [...availableResponsibilities.value, ...newItems];
    } else {
      availableResponsibilities.value = newItems;
    }
    
    hasMoreResponsibilities.value = response.data?.next !== null;
  } catch (error) {
    console.error("Error loading responsibilities:", error);
  } finally {
    isLoadingResponsibilities.value = false;
  }
}

function handleSearchResponsibilities(searchTerm) {
  responsibilitySearchTerm.value = searchTerm;
  responsibilityPage.value = 1;
  loadResponsibilities();
}

function handleLoadMoreResponsibilities() {
  responsibilityPage.value += 1;
  loadResponsibilities(true);
}

function handleClosePickResponsibility() {
  showPickResponsibilityDialog.value = false;
  selectedResponsibility.value = null;
}

async function handleResponsibilitySelected(payload) {
  if (!payload?.responsibility) return;
  
  selectedResponsibility.value = payload.responsibility;
  showPickResponsibilityDialog.value = false;
  
  try {
    proxy.$loader?.show();
    
    // First check if responsibility has active checkpoints
    const respDetailResponse = await getResponsibilityIdV2(
      accountId.value,
      selectedResponsibility.value.id,
      "responsibility"
    );
    
    const respDetail = respDetailResponse.data;
    
    if (respDetail) {
      // Check if checkpoints exist
      const totalCheckpoints = 
        (respDetail.active_apprentice_checkpoints || 0) +
        (respDetail.active_professional_checkpoints || 0) +
        (respDetail.active_coach_checkpoints || 0) +
        (respDetail.active_master_checkpoints || 0);
      
      // If no active checkpoints, show GiveBadgeDialog directly
      if (totalCheckpoints === 0) {
        // Show GiveBadgeDialog directly (description is computed from selectedResponsibility)
        showGiveBadgeDialog.value = true;
        proxy.$loader?.hide();
        return; // Exit early, don't load inspection data
      }
    }
    
    // If checkpoints exist, load inspection data and show InspectToAwardForm
    await loadInspectionData();
    showInspectForm.value = true;
  } catch (error) {
    console.error("Error loading inspection data:", error);
    proxy.$toast?.error("Error loading inspection data");
  } finally {
    proxy.$loader?.hide();
  }
}

async function loadInspectionData() {
  // Always initialize data first, even if some values are missing
  const extractCode = (codeStr) => {
    if (!codeStr && codeStr !== 0) return "";
    if (typeof codeStr === 'number') return `#${codeStr}`;
    const codeString = String(codeStr);
    const match = codeString.match(/#?(\d+)/);
    return match ? `#${match[1]}` : (codeString.startsWith('#') ? codeString : `#${codeString}`);
  };
  
  // Initialize with minimal data first
  const respCode = selectedResponsibility.value?.code 
    ? extractCode(selectedResponsibility.value.code) 
    : `#${selectedResponsibility.value?.id || ''}`;
  
  formattedResponsibility.value = {
    responsibilityCode: respCode,
    responsibilityName: selectedResponsibility.value?.name || "",
    responsibilityId: selectedResponsibility.value?.id || null,
    responsibilityImportance: 3,
    roleCode: "",
    roleName: "",
    roleId: "",
    roleImportance: 3,
    positionCode: "",
    positionName: employeeData.value?.primary_position?.name || employeeData.value?.title || "",
    positionId: "",
    positionImportance: 3,
    positionCommitment: "Primary",
    assignedOn: null,
    newDuration: 15,
    show_codes: true,
  };
  
  formattedEmployee.value = {
    firstName: employeeData.value?.first_name || "",
    lastName: employeeData.value?.last_name || "",
    position: employeeData.value?.primary_position?.name || employeeData.value?.title || "",
    photoUrl: employeeData.value?.avatar || employeeData.value?.photoUrl || "",
  };
  
  if (!accountId.value || !selectedResponsibility.value || !computedEmployeeId.value) {
    console.warn("Missing required data for inspection:", {
      accountId: accountId.value,
      selectedResponsibility: selectedResponsibility.value,
      computedEmployeeId: computedEmployeeId.value
    });
    // Data is already initialized above, just return
    return;
  }
  
  try {
    proxy.$loader?.show();
    
    // Update with actual data (already initialized above with minimal data)
    const respCode = selectedResponsibility.value.code 
      ? extractCode(selectedResponsibility.value.code) 
      : `#${selectedResponsibility.value.id}`;
    
    formattedResponsibility.value = {
      ...formattedResponsibility.value,
      responsibilityCode: respCode,
      responsibilityName: selectedResponsibility.value.name || "",
      responsibilityId: selectedResponsibility.value.id,
      responsibilityImportance: 3, // Default importance
      roleCode: "", // Empty if not available from frontend
      roleName: "", // Empty if not available from frontend
      roleId: "",
      roleImportance: 3,
      positionCode: "", // Empty if not available from frontend
      positionName: employeeData.value?.primary_position?.name || employeeData.value?.title || "", // Use employee's title as position name
      positionId: "",
      positionImportance: 3,
      positionCommitment: "Primary",
      assignedOn: null,
      newDuration: 15,
      show_codes: true,
    };
    
    // Update employee data (already initialized above)
    formattedEmployee.value = {
      ...formattedEmployee.value,
      firstName: employeeData.value?.first_name || "",
      lastName: employeeData.value?.last_name || "",
      position: employeeData.value?.primary_position?.name || employeeData.value?.title || "",
      photoUrl: employeeData.value?.avatar || employeeData.value?.photoUrl || "",
    };
    
    // Try to find current badge from employeeData badges if available (no API call)
    let currentBadgeData = null;
    if (employeeData.value?.badges && Array.isArray(employeeData.value.badges)) {
      currentBadgeData = employeeData.value.badges.find(
        (badge) => {
          const badgeRespId = badge.issued_for?.id || badge.responsibility?.id || badge.responsibility_id;
          return badgeRespId == selectedResponsibility.value.id && badge.is_current;
        }
      );
    }
    
    if (currentBadgeData) {
      const badgeLevel = currentBadgeData.level || currentBadgeData.currentBadge?.level || "novice";
      currentBadgeLevel.value = typeof badgeLevel === 'string' 
        ? badgeLevel.charAt(0).toUpperCase() + badgeLevel.slice(1)
        : getLevelName(badgeLevel);
      
      const statusMap = {
        active: "Good Standing",
        good_standing: "Good Standing",
        given: "Given",
        warning: "No Dot",
        issue: "No Dot",
        obsolete: "Obsolete",
        no_dot: "No Dot",
      };
      inspectionDot.value = statusMap[currentBadgeData.status] || "Good Standing";
    } else {
      currentBadgeLevel.value = "Novice";
      inspectionDot.value = "Good Standing";
    }
    
    // Get responsibility details with checkpoint counts
    try {
      const respDetailResponse = await getResponsibilityIdV2(
        accountId.value,
        selectedResponsibility.value.id,
        "responsibility"
      );
      
      const respDetail = respDetailResponse.data;
      
      if (respDetail) {
        // Update responsibility code and name from detail
        formattedResponsibility.value = {
          ...formattedResponsibility.value,
          responsibilityCode: respDetail.code_str || formattedResponsibility.value.responsibilityCode,
          responsibilityName: respDetail.name || formattedResponsibility.value.responsibilityName,
        };
        
        // Update level counter from checkpoint counts (using active checkpoints)
        inspectionLevelCounter.value = {
          Apprentice: respDetail.active_apprentice_checkpoints || respDetail.count_apprentice_checkpoints || 0,
          Professional: respDetail.active_professional_checkpoints || respDetail.count_professional_checkpoints || 0,
          Coach: respDetail.active_coach_checkpoints || respDetail.count_coach_checkpoints || 0,
          Master: respDetail.active_master_checkpoints || respDetail.count_master_checkpoints || 0,
        };
        
        // Check if checkpoints exist
        const totalCheckpoints = 
          (respDetail.active_apprentice_checkpoints || 0) +
          (respDetail.active_professional_checkpoints || 0) +
          (respDetail.active_coach_checkpoints || 0) +
          (respDetail.active_master_checkpoints || 0);
        
        // If no active checkpoints, show GiveBadgeDialog directly (handled in handleResponsibilitySelected)
        // This check is done earlier in handleResponsibilitySelected before calling loadInspectionData
        
        if (totalCheckpoints > 0) {
          // Load checkpoint evaluation data for this employee
          try {
            const evalResponse = await getListActiveCheckpointsEvalV2(
              accountId.value,
              selectedResponsibility.value.id,
              computedEmployeeId.value,
              100,
              0
            );
            
            if (evalResponse.data?.results) {
              inspectionData.value = evalResponse.data.results.map((item) => ({
                id: item.checkpoint,
                checkpoint_id: item.checkpoint,
                level: getLevelName(item.proficiency_level), // Use level name string, not number
                level_name: getLevelName(item.proficiency_level),
                proficiency_level: item.proficiency_level, // Keep number for reference
                criteria: item.checkpoint_criteria,
                instructions: item.checkpoint_instructions,
                video: (item.microlearning && typeof item.microlearning === 'string' && (item.microlearning.includes('<iframe') || item.microlearning.includes('http'))) 
                  ? item.microlearning 
                  : "", // Only use if it's a valid iframe string or URL, otherwise empty string shows "No microlearning content available"
                status: item.status,
                evaluation_status: item.status,
                inspectionStatus: getInspectionStatusView(item.status),
                isPassed: item.status === "passed",
                isFailed: item.status === "failed",
                isNotApplicable: item.status === "not_applicable",
                failed_by: item.evaluated_by?.name || null,
                failed_on: item.evaluated_on,
                failed_reason: item.reason,
                first_name: item.evaluated_by?.first_name || item.evaluated_by?.name?.split(' ')[0] || "",
                last_name: item.evaluated_by?.last_name || item.evaluated_by?.name?.split(' ').slice(1).join(' ') || "",
                updated_on: item.evaluated_on,
                updated_by: item.evaluated_by || null,
                avatar: item.evaluator_avatar || item.evaluated_by?.picture || "",
                not_applicable_reason: item.status === "not_applicable" ? item.reason : "",
              }));
            }
            
            showNoInspectionRecords.value = inspectionData.value.length === 0;
            showInspectionPagination.value = evalResponse.data?.count > 10;
            inspectionPagination.value = {
              total: evalResponse.data?.count || 0,
              page: 1,
              perPage: 10,
            };
          } catch (evalError) {
            console.warn("Could not load checkpoint evaluation data:", evalError);
            // If evaluation fails, try to get checklist data at least
            try {
              const checklistResponse = await getResponsibilityChecklistV2(
                accountId.value,
                selectedResponsibility.value.id,
                { status: "active" }
              );
              
              if (checklistResponse.data && Array.isArray(checklistResponse.data)) {
                inspectionData.value = checklistResponse.data.map((item) => ({
                  id: item.id,
                  checkpoint_id: item.id,
                  level: getLevelName(item.proficiency_level), // Use level name string, not number
                  level_name: getLevelName(item.proficiency_level),
                  proficiency_level: item.proficiency_level, // Keep number for reference
                  criteria: item.criteria,
                  instructions: item.instructions,
                  video: (item.microlearning && typeof item.microlearning === 'string' && (item.microlearning.includes('<iframe') || item.microlearning.includes('http'))) 
                  ? item.microlearning 
                  : "", // Only use if it's a valid iframe string or URL, otherwise empty string shows "No microlearning content available"
                  status: item.status,
                  evaluation_status: "pending", // Default since no evaluation data
                  inspectionStatus: "pending-evaluation",
                  isPassed: false,
                  isFailed: false,
                  isNotApplicable: false,
                  failed_by: null,
                  failed_on: null,
                  failed_reason: null,
                  first_name: "",
                  last_name: "",
                  updated_on: null,
                  updated_by: null,
                  avatar: "",
                  not_applicable_reason: "",
                  obsolete_reason: "",
                  reset_reason: "",
                }));
              }
              
              showNoInspectionRecords.value = inspectionData.value.length === 0;
            } catch (checklistError) {
              console.warn("Could not load checklist data:", checklistError);
              inspectionData.value = [];
              showNoInspectionRecords.value = true;
            }
          }
        } else {
          // No checkpoints exist
          inspectionData.value = [];
          showNoInspectionRecords.value = true;
        }
      }
    } catch (respError) {
      console.warn("Could not load responsibility details:", respError);
      // Set empty data if API fails
      inspectionData.value = [];
      inspectionLevelCounter.value = {
        Apprentice: 0,
        Professional: 0,
        Coach: 0,
        Master: 0,
      };
      showNoInspectionRecords.value = true;
    }
    
    showInspectionPagination.value = false;
    inspectionPagination.value = {
      total: inspectionData.value.length,
      page: 1,
      perPage: 10,
    };
  } catch (error) {
    console.error("Error loading inspection data:", error);
    proxy.$toast?.error("Error loading inspection data");
  } finally {
    proxy.$loader?.hide();
  }
}

function getLevelName(level) {
  const levelMap = {
    1: "Apprentice",
    2: "Professional",
    3: "Coach",
    4: "Master",
  };
  return levelMap[level] || "Apprentice";
}

function getInspectionStatusView(evaluationStatus) {
  if (!evaluationStatus) return "pending-evaluation";
  
  const statusMap = {
    passed: "passed-evaluation",
    failed: "failed-evaluation",
    not_applicable: "notapplicable-evaluation",
    reset: "reset-pending-evaluation",
    pending: "pending-evaluation",
  };
  
  return statusMap[evaluationStatus] || "pending-evaluation";
}

// Remove Badge Handlers
function handleRemoveBadgeClick(badgeData) {
  // badgeData is the full badge object from EmployeeBadgeHolderList
  // Structure: { id, currentBadge: { level }, responsibility: { name }, ... }
  
  if (!badgeData || !badgeData.id) {
    console.error("Invalid badge data:", badgeData);
    proxy.$toast?.error("Invalid badge data");
    return;
  }

  // Extract badge level from currentBadge.level
  const badgeLevel = badgeData.currentBadge?.level || badgeData.level || "";
  
  selectedBadgeForRemoval.value = {
    id: badgeData.id, // Badge ID is at top level
    badgeName: getBadgeLevelName(badgeLevel),
    level: badgeLevel,
    responsibilityName: badgeData.responsibility?.name || "",
    responsibility: badgeData.responsibility || null,
  };
  
  showRemoveBadgeDialog.value = true;
}

function getBadgeLevelName(level) {
  if (typeof level === 'string') {
    return level;
  }
  const levelMap = {
    1: "Apprentice",
    2: "Professional",
    3: "Coach",
    4: "Master",
  };
  return levelMap[level] || "Apprentice";
}

async function handleConfirmRemoveBadge({ reason: removeReason }) {
  if (!selectedBadgeForRemoval.value || !selectedBadgeForRemoval.value.id || !removeReason?.trim()) {
    proxy.$toast?.error("Please provide a reason for removing the badge");
    return;
  }

  proxy.$loader?.show();
  try {
    const payload = {
      badge: selectedBadgeForRemoval.value.id,
      method: "removed",
      reason: removeReason.trim(),
    };

    await deleteResponsibilityBadge(
      accountId.value,
      selectedBadgeForRemoval.value.id,
      payload
    );

    proxy.$toast?.success("Badge removed successfully");
    
    // Refresh employee data to update badge list
    await fetchEmployeeData();
    
    // Refresh badge list without page reload
    await refreshBadgeList();
    
    showRemoveBadgeDialog.value = false;
    selectedBadgeForRemoval.value = null;
  } catch (error) {
    console.error("Error removing badge:", error);
    const errorMessage = error?.response?.data?.message || error?.response?.data?.level?.[0] || error?.message || "Error removing badge";
    proxy.$toast?.error(errorMessage);
  } finally {
    proxy.$loader?.hide();
  }
}

function handleCloseRemoveBadgeDialog() {
  showRemoveBadgeDialog.value = false;
  selectedBadgeForRemoval.value = null;
}

// Refresh badge list helper function
async function refreshBadgeList() {
  try {
    // Access EmployeeBadges component via ref and call refresh method
    if (employeeBadgesRef.value && typeof employeeBadgesRef.value.refreshBadges === 'function') {
      await employeeBadgesRef.value.refreshBadges();
    }
  } catch (error) {
    console.warn("Could not refresh badge list:", error);
    // Don't show error to user, just log it
  }
}

// Inspection Form Handlers
function handleInspectionBreadcrumbClick(breadcrumb) {
  if (breadcrumb.text === "Employee") {
    handleCloseInspection();
    // Navigate back to badges tab
    selectedTab.value = "badges";
  }
}

async function handleBadgeSelectedFromInspection(payload) {
  if (!accountId.value || !computedEmployeeId.value || !selectedResponsibility.value) {
    return;
  }
  
  // Check if this is a remove badge action
  if (payload?.can_remove) {
    // Handle remove badge if needed
    return;
  }
  
  try {
    proxy.$loader?.show();
    
    // Extract level from payload - it can be in value, Label, or level property
    let levelString = null;
    if (payload?.value) {
      levelString = payload.value;
    } else if (payload?.Label) {
      levelString = payload.Label;
    } else if (payload?.level) {
      levelString = payload.level;
    } else if (typeof payload === 'string') {
      levelString = payload;
    }
    
    if (!levelString) {
      console.error("No level found in badge payload:", payload);
      proxy.$toast?.error("Invalid badge selection");
      return;
    }
    
    const level = getBadgeLevelNumber(levelString);
    const badgePayload = {
      employee: computedEmployeeId.value,
      responsibility: selectedResponsibility.value.id,
      level: level,
      given: false,
      issued_method: "awarded",
      notify: payload?.notify || false,
    };
    
    await createProficiencyBadge(accountId.value, badgePayload);
    
    // Close inspection form and return to badges tab
    handleCloseInspection();
    selectedTab.value = "badges";
    
    // Refresh employee data and badges
    await fetchEmployeeData();
    handleEmployeeUpdate();
    
    // Refresh badge list without page reload
    await refreshBadgeList();
    
    proxy.$toast?.success("Badge awarded successfully");
  } catch (error) {
    console.error("Error awarding badge:", error);
    proxy.$toast?.error("Error awarding badge");
  } finally {
    proxy.$loader?.hide();
  }
}

function getBadgeLevelNumber(level) {
  if (!level || typeof level !== 'string') {
    console.error("Invalid level provided:", level);
    return 1; // Default to Apprentice
  }
  
  const levelMap = {
    apprentice: 1,
    professional: 2,
    coach: 3,
    master: 4,
  };
  
  const normalizedLevel = level.toLowerCase().trim();
  return levelMap[normalizedLevel] || 1;
}

function handleInspectionSearch(searchTerm) {
  inspectionFilterText.value = searchTerm;
}

function handleInspectionClick(event) {
  // Handle inspection click events
}

function handleInspectionRemoveFilter(filter) {
  // Handle filter removal
}

function handleInspectionFilterTypes(filterType) {
  inspectionFilterType.value = filterType;
}

function handleInspectionPagination(pagination) {
  inspectionPagination.value = pagination;
}

function handleInspectionClearSelection() {
  inspectionChipText.value = [];
}

function handleInspectionCheckedItemsChanged(items) {
  // Handle checked items changed
}

// Handle Pass
async function handleInspectionPass(row) {
  try {
    proxy.$loader?.show();
    
    const chkpId = row.id || row.checkpoint_id || row.checkpoint;
    const previousReadyLevel = readyForBadgeLevel.value;
    
    const dataPayload = {
      employee: computedEmployeeId.value,
      checkpoint: chkpId,
      reason: "passed",
      status: "passed",
    };
    
    await passCheckPointEvaluation(
      accountId.value,
      selectedResponsibility.value.id,
      chkpId,
      dataPayload
    );
    
    // Reload checkpoint data
    await reloadInspectionData();
    
    // Check if readyForLevel changed (employee is ready for next badge)
    const newReadyLevel = readyForBadgeLevel.value;
    if (newReadyLevel && newReadyLevel !== previousReadyLevel) {
      proxy.$toast?.success(`Employee is now ready for ${newReadyLevel} badge!`);
    }
    
    proxy.$toast?.success("Checkpoint passed successfully");
  } catch (error) {
    console.error("Error passing checkpoint:", error);
    proxy.$toast?.error("Error passing checkpoint");
  } finally {
    proxy.$loader?.hide();
  }
}

// Handle Fail - opens reason modal first
function handleInspectionFail(row) {
  selectedCheckpoint.value = row;
  reason.value = row.failed_reason && row.evaluation_status === "failed" 
    ? row.failed_reason 
    : "";
  openReasonModal("fail");
}

// Handle Not Applicable - opens reason modal first
function handleInspectionNotApplicable(row) {
  selectedCheckpoint.value = row;
  reason.value = "";
  openReasonModal("notApplicable");
}

// Reason Modal handlers
function openReasonModal(type) {
  reasonModal.value.type = type;
  reasonModal.value.config = modalConfigs[type];
  reasonModal.value.show = true;
}

function closeReasonModal() {
  reasonModal.value.show = false;
  reasonModal.value.type = null;
  reasonModal.value.config = {};
  reason.value = "";
  selectedCheckpoint.value = null;
}

async function handleReasonAction() {
  if (reasonModal.value.type === "fail") {
    await executeFail(selectedCheckpoint.value);
  } else if (reasonModal.value.type === "notApplicable") {
    await executeNotApplicable(selectedCheckpoint.value);
  }
}

// Execute Fail API call
async function executeFail(checkpointData) {
  if (!reason.value || reason.value.trim() === "") {
    proxy.$toast?.error("Please provide a reason");
    return;
  }
  
  try {
    proxy.$loader?.show();
    
    const chkpId = checkpointData.id || checkpointData.checkpoint_id || checkpointData.checkpoint;
    
    const dataPayload = {
      employee: computedEmployeeId.value,
      checkpoint: chkpId,
      reason: reason.value,
      status: "failed",
    };
    
    await failCheckPointEvaluation(
      accountId.value,
      selectedResponsibility.value.id,
      chkpId,
      dataPayload
    );
    
    closeReasonModal();
    
    // Reload checkpoint data
    await reloadInspectionData();
    
    proxy.$toast?.success("Checkpoint marked as failed");
  } catch (error) {
    console.error("Error failing checkpoint:", error);
    proxy.$toast?.error("Error failing checkpoint");
  } finally {
    proxy.$loader?.hide();
  }
}

// Execute Not Applicable API call
async function executeNotApplicable(checkpointData) {
  if (!reason.value || reason.value.trim() === "") {
    proxy.$toast?.error("Please provide a reason");
    return;
  }
  
  try {
    proxy.$loader?.show();
    
    const chkpId = checkpointData.id || checkpointData.checkpoint_id || checkpointData.checkpoint;
    
    const dataPayload = {
      employee: computedEmployeeId.value,
      checkpoint: chkpId,
      reason: reason.value,
      status: "not_applicable",
    };
    
    await notApplicableCheckPointEvaluation(
      accountId.value,
      selectedResponsibility.value.id,
      chkpId,
      dataPayload
    );
    
    closeReasonModal();
    
    // Reload checkpoint data
    await reloadInspectionData();
    
    proxy.$toast?.success("Checkpoint marked as not applicable");
  } catch (error) {
    console.error("Error marking checkpoint as not applicable:", error);
    proxy.$toast?.error("Error marking checkpoint as not applicable");
  } finally {
    proxy.$loader?.hide();
  }
}

// Reload inspection data after checkpoint evaluation
async function reloadInspectionData() {
  if (!accountId.value || !selectedResponsibility.value || !computedEmployeeId.value) return;
  
  try {
    // Reload checkpoint evaluation data
    const evalResponse = await getListActiveCheckpointsEvalV2(
      accountId.value,
      selectedResponsibility.value.id,
      computedEmployeeId.value,
      100,
      0
    );
    
    if (evalResponse.data?.results) {
      inspectionData.value = evalResponse.data.results.map((item) => ({
        id: item.checkpoint,
        checkpoint_id: item.checkpoint,
        level: getLevelName(item.proficiency_level), // Use level name string, not number
        level_name: getLevelName(item.proficiency_level),
        proficiency_level: item.proficiency_level, // Keep number for reference
                criteria: item.checkpoint_criteria,
                instructions: item.checkpoint_instructions,
                video: (item.microlearning && typeof item.microlearning === 'string' && (item.microlearning.includes('<iframe') || item.microlearning.includes('http'))) 
                  ? item.microlearning 
                  : "", // Only use if it's a valid iframe string or URL, otherwise empty string shows "No microlearning content available"
                status: item.status,
                evaluation_status: item.status,
                inspectionStatus: getInspectionStatusView(item.status),
                isPassed: item.status === "passed",
                isFailed: item.status === "failed",
                isNotApplicable: item.status === "not_applicable",
                failed_by: item.evaluated_by?.name || null,
                failed_on: item.evaluated_on,
                failed_reason: item.reason,
                first_name: item.evaluated_by?.first_name || item.evaluated_by?.name?.split(' ')[0] || "",
                last_name: item.evaluated_by?.last_name || item.evaluated_by?.name?.split(' ').slice(1).join(' ') || "",
                updated_on: item.evaluated_on,
                updated_by: item.evaluated_by || null,
                avatar: item.evaluator_avatar || item.evaluated_by?.picture || "",
                not_applicable_reason: item.status === "not_applicable" ? item.reason : "",
                obsolete_reason: "",
                reset_reason: "",
              }));
    }
    
    // Also reload responsibility details to get updated readyForLevel
    const respDetailResponse = await getResponsibilityIdV2(
      accountId.value,
      selectedResponsibility.value.id,
      "responsibility"
    );
    
    // Try to get updated badge info (this might need employee badges API)
    // For now, we'll keep the current badge level
  } catch (error) {
    console.warn("Error reloading inspection data:", error);
  }
}

function handleCloseInspection() {
  showInspectForm.value = false;
  selectedResponsibility.value = null;
  inspectionData.value = [];
  inspectionFilterText.value = "";
  formattedResponsibility.value = null;
  formattedEmployee.value = null;
  currentBadgeLevel.value = null;
  inspectionDot.value = "Good Standing";
  // Return to badges tab
  selectedTab.value = "badges";
}

// Give Badge Dialog Handlers
function handleCloseGiveBadge() {
  showGiveBadgeDialog.value = false;
  selectedResponsibility.value = null;
  selectedBadgeLevel.value = "apprentice";
}

async function handleGiveBadge(payload) {
  if (!accountId.value || !computedEmployeeId.value || !selectedResponsibility.value) {
    return;
  }
  
  try {
    proxy.$loader?.show();
    
    // Convert level string to integer (API requires integer: 1=Apprentice, 2=Professional, 3=Coach, 4=Master)
    const levelNumber = getBadgeLevelNumber(payload.level);
    
    if (!levelNumber || levelNumber === 0) {
      proxy.$toast?.error("Invalid badge level selected");
      proxy.$loader?.hide();
      return;
    }
    
    const badgePayload = {
      employee: computedEmployeeId.value,
      responsibility: selectedResponsibility.value.id,
      level: levelNumber, // Use integer instead of string
      given: true,
      issued_method: "given",
      notify: payload.notifyEmployee || false,
    };
    
    await createProficiencyBadge(accountId.value, badgePayload);
    
    // Close dialog
    showGiveBadgeDialog.value = false;
    selectedResponsibility.value = null;
    
    // Refresh employee data and badges
    await fetchEmployeeData();
    handleEmployeeUpdate();
    
    // Refresh badge list without page reload
    await refreshBadgeList();
    
    proxy.$toast?.success("Badge issued successfully");
  } catch (error) {
    console.error("Error giving badge:", error);
    proxy.$toast?.error("Error issuing badge");
  } finally {
    proxy.$loader?.hide();
  }
}

// Watch for employee data updates from store (e.g., after readiness recalculation, status change, or position assignment)
watch(
  () => employeeStore.currentEmployee,
  (newEmployee) => {
    if (newEmployee && newEmployee.id == computedEmployeeId.value) {
      // Normalize status to ensure both status and current_status are set
      const normalizedEmployee = {
        ...newEmployee,
        status: newEmployee.status || newEmployee.current_status,
        current_status: newEmployee.current_status || newEmployee.status,
      };
      
      // Replace entire object to ensure deep reactivity (especially for nested properties like primary_position)
      // This ensures changes to primary_position are properly detected
      employeeData.value = { ...normalizedEmployee };
      
      // If status is terminated and child component is visible, trigger its status change handler
      const statusToCheck = (
        normalizedEmployee.current_status ||
        normalizedEmployee.status ||
        ""
      ).toLowerCase();
      
      if (statusToCheck === "terminated" && selectedTab.value === "pathtoreadiness") {
        nextTick(() => {
          if (pathToReadinessRef.value && typeof pathToReadinessRef.value.handleEmployeeStatusChanged === 'function') {
            pathToReadinessRef.value.handleEmployeeStatusChanged();
          }
        });
      }
    }
  },
  { deep: true, immediate: true }
);

// Explicitly watch for primary_position changes to ensure reactivity
watch(
  () => employeeStore.currentEmployee?.primary_position,
  (newPosition) => {
    const storeEmployee = employeeStore.currentEmployee;
    if (storeEmployee && storeEmployee.id == computedEmployeeId.value && employeeData.value) {
      // Update primary_position in local employeeData to trigger reactivity
      employeeData.value = {
        ...employeeData.value,
        primary_position: newPosition ? { ...newPosition } : null,
      };
    }
  },
  { deep: true }
);

// Watch for employeeId changes and refetch data
watch(
  computedEmployeeId,
  async (newEmployeeId, oldEmployeeId) => {
    if (newEmployeeId && newEmployeeId !== oldEmployeeId && accountId.value) {
      try {
        proxy.$loader?.show();
        employeeId.value = newEmployeeId;
        employeeData.value = null;
        selectedTab.value = "pathtoreadiness";
        await fetchEmployeeData();
      } catch (error) {
        console.error("Error fetching employee data on change:", error);
      } finally {
        proxy.$loader?.hide();
      }
    }
  },
  { immediate: false }
);

// Also watch route query for employeeId changes
watch(
  () => proxy?.$route?.query?.employeeId,
  async (newEmployeeId, oldEmployeeId) => {
    if (newEmployeeId && newEmployeeId !== oldEmployeeId && accountId.value) {
      try {
        proxy.$loader?.show();
        employeeId.value = newEmployeeId;
        employeeData.value = null;
        
        // Check if there's a specific tab in the query, otherwise default to pathtoreadiness
        const employeeTabFromQuery = proxy?.$route?.query?.employeeTab;
        if (employeeTabFromQuery) {
          const validTabs = [
            "pathtoreadiness",
            "responsibilities",
            "badges",
            "positionshistory",
          ];
          if (validTabs.includes(employeeTabFromQuery.toLowerCase())) {
            selectedTab.value = employeeTabFromQuery.toLowerCase();
          } else {
            selectedTab.value = "pathtoreadiness";
          }
        } else {
          selectedTab.value = "pathtoreadiness";
        }
        
        await fetchEmployeeData();
      } catch (error) {
        console.error("Error fetching employee data on route change:", error);
      } finally {
        proxy.$loader?.hide();
      }
    }
  },
  { immediate: false }
);

// Watch for employeeTab query parameter changes to sync selected tab
watch(
  () => proxy?.$route?.query?.employeeTab,
  (newTab) => {
    if (newTab) {
      const validTabs = [
        "pathtoreadiness",
        "responsibilities",
        "badges",
        "positionshistory",
      ];
      if (validTabs.includes(newTab.toLowerCase())) {
        selectedTab.value = newTab.toLowerCase();
      }
    } else {
      // Default to pathtoreadiness if no tab specified
      selectedTab.value = "pathtoreadiness";
    }
  },
  { immediate: true, deep: true }
);

// Watch for employeeId changes to reset tab when switching employees
watch(
  () => computedEmployeeId.value,
  (newEmployeeId, oldEmployeeId) => {
    if (newEmployeeId && newEmployeeId !== oldEmployeeId) {
      // When switching to a new employee, check route for tab preference
      const employeeTabFromQuery = proxy?.$route?.query?.employeeTab;
      if (employeeTabFromQuery) {
        const validTabs = [
          "pathtoreadiness",
          "responsibilities",
          "badges",
          "positionshistory",
        ];
        if (validTabs.includes(employeeTabFromQuery.toLowerCase())) {
          selectedTab.value = employeeTabFromQuery.toLowerCase();
        }
      } else {
        // Default to pathtoreadiness when switching employees
        selectedTab.value = "pathtoreadiness";
      }
    }
  }
);

onMounted(async () => {
  try {
    proxy.$loader?.show();
    accountId.value = JSON.parse(localStorage.getItem("currentAccountID"));
    employeeId.value = computedEmployeeId.value;

    // Initialize tab from query parameter
    const employeeTabFromQuery = proxy?.$route?.query?.employeeTab;
    if (employeeTabFromQuery) {
      const validTabs = [
        "pathtoreadiness",
        "responsibilities",
        "badges",
        "positionshistory",
      ];
      if (validTabs.includes(employeeTabFromQuery.toLowerCase())) {
        selectedTab.value = employeeTabFromQuery.toLowerCase();
      }
    } else {
      // Default to pathtoreadiness if no tab specified
      selectedTab.value = "pathtoreadiness";
    }

    await fetchEmployeeData();
  } finally {
    proxy.$loader?.hide();
  }
});
</script>

<style scoped>
.employee-detail {
  background: #fff;
  border-radius: 8px;
  width: 100%;
  border: 1px solid #eaecf0;
  box-shadow: 0px 1px 2px 0px #1018280f, 0px 1px 3px 0px #1018281a;
  padding: 16px;
  border-radius: 8px;
  background: #fff;
  width: 100%;
  border: 1px solid #eaecf0;
}

.employee-page-header .employee-card-header-chatter-link {
  left: unset !important;
  top: unset !important;
}

.employee-detail-content {
  overflow: visible;
  height: auto;
}

.tab-content-wrapper {
  width: 100%;
}

/* Hide ALL headers in child views - be very aggressive */
.tab-content-wrapper ::v-deep .header-wrapper,
.tab-content-wrapper
  ::v-deep
  .employee-card-path-to-readiness
  > .header-wrapper,
.tab-content-wrapper ::v-deep .v-container .header-wrapper,
.tab-content-wrapper
  ::v-deep
  .employee-card-path-to-readiness
  > *:first-child.header-wrapper {
  display: none !important;
  visibility: hidden !important;
  height: 0 !important;
  overflow: hidden !important;
}

/* Remove v-container padding and make it full width */
.tab-content-wrapper ::v-deep .v-container {
  padding: 0 !important;
  margin: 0 !important;
  max-width: 100% !important;
  width: 100% !important;
}

/* Hide PageTitle components */
.tab-content-wrapper ::v-deep PageTitle {
  display: none !important;
}

/* Remove padding from content wrappers */
.tab-content-wrapper ::v-deep .employee-card-path-to-readiness {
  padding: 0 !important;
  margin: 0 !important;
}

.tab-content-wrapper ::v-deep .custom-path {
  padding: 0 !important;
  margin: 0 !important;
}

.tab-content-wrapper ::v-deep .emp-assignments-view {
  padding: 0 !important;
  margin: 0 !important;
}

/* Inspect Form Page Styles */
.inspect-form-page-wrapper {
  width: 100%;
  margin-top: 16px;
}
</style>

<style>
.employee-page-header .employee-card-header-chatter-link {
  position: unset !important;
}
</style>
